import { Editor } from '@tiptap/react';

import { useCallback, useEffect } from 'react';

import { VarMappingPropsWithValidation } from '@/components/EvaluatorCrudPane/types';

interface UseVariableMappingsParams {
  editor: Editor | null | undefined;
  hideSyntaxHighlighting?: boolean;
  variableMappingProps?: VarMappingPropsWithValidation;
}

export function useVariableMappings({
  editor,
  hideSyntaxHighlighting,
  variableMappingProps,
}: UseVariableMappingsParams) {
  /**
   * Proxy to editor.commands.updateVariableMappings
   */
  const updateVariableMappings = useCallback(
    (
      variableMappings: Record<string, string>,
      storeType: VarMappingPropsWithValidation['storeType']
    ) => {
      if (
        !hideSyntaxHighlighting &&
        typeof editor?.commands.updateVariableMappings === 'function' &&
        variableMappings
      ) {
        editor.commands.updateVariableMappings(variableMappings, storeType);
      }
    },
    [editor, hideSyntaxHighlighting]
  );

  /**
   * Proxy to editor.commands.updateAvailablePaths
   */
  const updateAvailablePaths = useCallback(
    (availablePaths?: string[]) => {
      if (
        !hideSyntaxHighlighting &&
        typeof editor?.commands.updateAvailablePaths === 'function' &&
        availablePaths
      ) {
        editor.commands.updateAvailablePaths(availablePaths);
      }
    },
    [editor, hideSyntaxHighlighting]
  );

  /**
   * Proxy to editor.commands.updateUneditableVariables
   */
  const updateUneditableVariables = useCallback(
    (uneditableVariables?: string[]) => {
      if (
        !hideSyntaxHighlighting &&
        typeof editor?.commands.updateUneditableVariables === 'function' &&
        uneditableVariables
      ) {
        editor.commands.updateUneditableVariables(uneditableVariables);
      }
    },
    [editor, hideSyntaxHighlighting]
  );

  /**
   * Proxy to editor.commands.updateVariables
   */
  const updateVariables = useCallback(
    (variables?: string[]) => {
      if (Array.isArray(variables)) {
        const cmds = editor?.commands;
        if (typeof cmds?.updateVariables === 'function') {
          cmds.updateVariables(variables);
        }
      }
    },
    [editor]
  );

  /**
   * Proxy to editor.commands.updateIsSuggestions
   */
  const updateIsSuggestions = useCallback(
    (isSuggestions?: boolean) => {
      if (
        !hideSyntaxHighlighting &&
        typeof editor?.commands.updateIsSuggestions === 'function' &&
        typeof isSuggestions === 'boolean'
      ) {
        editor.commands.updateIsSuggestions(isSuggestions);
      }
    },
    [editor, hideSyntaxHighlighting]
  );

  // Register callbacks on mount/update
  useEffect(() => {
    if (
      !hideSyntaxHighlighting &&
      typeof editor?.commands.registerUpdateCallbacks === 'function' &&
      variableMappingProps
    ) {
      const { setVariableMappings, setActiveVariable } = variableMappingProps;
      editor.commands.registerUpdateCallbacks({
        setVariableMappings,
        setActiveVariable,
      });
    }
  }, [editor, variableMappingProps, hideSyntaxHighlighting]);

  // Keep available paths & uneditable variables up to date
  useEffect(() => {
    updateAvailablePaths(variableMappingProps?.availablePaths ?? []);
    updateUneditableVariables(variableMappingProps?.uneditableVariables ?? []);
  }, [
    variableMappingProps?.availablePaths,
    variableMappingProps?.uneditableVariables,
    updateAvailablePaths,
    updateUneditableVariables,
  ]);

  // Keep variable mappings in sync
  useEffect(() => {
    if (variableMappingProps?.variableMappings) {
      updateVariableMappings(
        variableMappingProps.variableMappings,
        variableMappingProps.storeType
      );
    }
  }, [
    variableMappingProps?.variableMappings,
    variableMappingProps?.storeType,
    updateVariableMappings,
  ]);

  useEffect(() => {
    if (variableMappingProps?.showValidationErrors != null) {
      editor?.commands.updateShowValidationErrors(
        variableMappingProps.showValidationErrors
      );
    }
  }, [variableMappingProps?.showValidationErrors, editor]);

  // Keep isSuggestions in sync
  useEffect(() => {
    if (variableMappingProps?.isSuggestions != null) {
      updateIsSuggestions(variableMappingProps.isSuggestions);
    }
  }, [variableMappingProps?.isSuggestions, updateIsSuggestions]);

  return {
    updateVariableMappings,
    updateAvailablePaths,
    updateUneditableVariables,
    updateVariables,
    updateIsSuggestions,
  };
}
